/**
 * "+ New functional area" - the Questions-header control that creates a new Business-Process
 * area in the questionnaire. Areas are materialized from their questions (there is no area row),
 * so the dialog asks for the area name AND its first question and captures them together - the
 * area appears fully real, with no placeholder junk in the model. Further questions are added
 * from the area's own "+" in the sidebar (or /problem inside the area on the Scope tab).
 */

import * as React from 'react';
import { Button, Dialog, PlusIcon, RequiredLabel, TextField, Tooltip } from '@protoss/ui';

import { salesApi } from '../services/salesApi';

/** Labelled field wrapper matching the questionnaire dialogs' `sales-create-deal__field-*` markup. */
function LabelledField({
  label,
  children,
}: {
  label: string;
  children: (fieldId: string) => React.ReactNode;
}): React.ReactElement {
  const fieldId = React.useId();
  return (
    <div className="sales-create-deal__field-block">
      <div className="sales-create-deal__field-header">
        <label htmlFor={fieldId} className="sales-create-deal__field-label">
          <RequiredLabel label={label} required />
        </label>
      </div>
      {children(fieldId)}
    </div>
  );
}

export function AddFunctionalAreaControl({
  dealUuid,
  onReloadQuestionnaire,
}: {
  dealUuid: string;
  onReloadQuestionnaire: () => Promise<void>;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [areaName, setAreaName] = React.useState('');
  const [questionLabel, setQuestionLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canSubmit = areaName.trim().length > 0 && questionLabel.trim().length > 0 && !busy;
  const reset = () => {
    setAreaName('');
    setQuestionLabel('');
    setError(null);
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await salesApi.captureAdhocFields(dealUuid, [
        {
          label: questionLabel.trim(),
          uiControl: 'textarea',
          scopeMeta: { businessProcess: areaName.trim() },
        },
      ]);
      setOpen(false);
      reset();
      await onReloadQuestionnaire();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create the functional area.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      {/* Mirror the header's working tooltips (DealMain's Answered/All toggles) exactly: a PLAIN
          button trigger (the dialog opens via controlled state, not Dialog.Trigger) - nesting
          Tooltip.Trigger asChild around Dialog.Trigger asChild made Radix re-measure mid-open and
          paint the tooltip clipped. */}
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className="sales-create-deal__adhoc-add-icon"
            aria-label="New functional area"
            onClick={() => setOpen(true)}
          >
            <PlusIcon />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
            New functional area
            <Tooltip.Arrow className="ui-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      <Dialog.Portal>
        <Dialog.Overlay className="pi-dialog-overlay" />
        <Dialog.Content className="pi-dialog-content">
          <div className="pi-modal__header">
            <Dialog.Title className="pi-modal__title">New functional area</Dialog.Title>
            <Dialog.Close asChild>
              <button className="pi-modal__close" aria-label="Close">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="pi-modal__body-text">
            A functional area groups scope items (e.g. Payroll Operations). It needs at least one
            question - add more from the area&apos;s + in the sidebar.
          </Dialog.Description>
          <LabelledField label="Area name">
            {(fieldId) => (
              <TextField
                id={fieldId}
                value={areaName}
                placeholder="e.g. Payroll Operations"
                size="comfortable"
                onChange={(e) => setAreaName(e.target.value)}
              />
            )}
          </LabelledField>
          <LabelledField label="First question">
            {(fieldId) => (
              <TextField
                id={fieldId}
                value={questionLabel}
                placeholder="e.g. Payroll sync issues"
                size="comfortable"
                onChange={(e) => setQuestionLabel(e.target.value)}
              />
            )}
          </LabelledField>
          {error ? <p className="pi-modal__error">{error}</p> : null}
          <div className="pi-modal__actions pi-modal__actions--flush">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={busy}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleCreate()}
              disabled={!canSubmit}
            >
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
